import React, { useState } from 'react';
import { 
  Offcanvas, 
  Carousel, 
  Badge, 
  Button, 
  Row, 
  Col,
  ListGroup,
  Card
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faBath,
  faRuler,
  faCalendar,
  faHeart,
  faShare,
  faPhone,
  faEnvelope,
  faCalendarCheck,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faHome,
  faCar,
  faMapMarkerAlt,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import './PropertyDetailsPanel.css';
import { Property } from '../../types/property';
import { isPropertyAddress, safeFormatPrice, getAddressString, getCity, getState } from '../../types/propertyUtils';

interface PropertyDetailsPanelProps {
  show: boolean;
  properties: Property[];
  onHide: () => void;
  onPropertySelect?: (property: Property) => void;
  onScheduleTour?: (property: Property) => void;
  onSaveProperty?: (property: Property) => void;
}

const PropertyDetailsPanel: React.FC<PropertyDetailsPanelProps> = ({
  show,
  properties,
  onHide,
  onPropertySelect,
  onScheduleTour,
  onSaveProperty
}) => {
  const [isSaved, setIsSaved] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  console.log('🏠 PropertyDetailsPanel render:', { show, properties });
  
  // Properties should always be an array now
  const currentProperty = properties[selectedIndex] || properties[0];
  
  console.log('🏠 Current property:', currentProperty);
  console.log('🏠 Current property images:', currentProperty?.images);
  console.log('🏠 Is images array?:', Array.isArray(currentProperty?.images));
  
  if (!currentProperty || properties.length === 0) return null;

  const handleSave = () => {
    setIsSaved(!isSaved);
    if (onSaveProperty) {
      onSaveProperty(currentProperty);
    }
  };

  const handleScheduleTour = () => {
    if (onScheduleTour) {
      onScheduleTour(currentProperty);
    }
  };

  const handleNext = () => {
    if (selectedIndex < properties.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      if (onPropertySelect) {
        onPropertySelect(properties[selectedIndex + 1]);
      }
    }
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      if (onPropertySelect) {
        onPropertySelect(properties[selectedIndex - 1]);
      }
    }
  };


  return (
    <Offcanvas 
      show={show} 
      onHide={onHide} 
      placement="end" 
      className="property-details-panel"
    >
      <Offcanvas.Header className="panel-header">
        <div className="header-content">
          <Button 
            variant="link" 
            onClick={onHide}
            className="close-btn"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
          
          {properties.length > 1 && (
            <div className="property-navigation">
              <Button
                variant="link"
                onClick={handlePrevious}
                disabled={selectedIndex === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <span className="property-count">
                {selectedIndex + 1} of {properties.length}
              </span>
              <Button
                variant="link"
                onClick={handleNext}
                disabled={selectedIndex === properties.length - 1}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          )}
          
          <div className="header-actions">
            <Button
              variant="link"
              onClick={handleSave}
              className={`save-btn ${isSaved ? 'saved' : ''}`}
            >
              <FontAwesomeIcon icon={faHeart} />
            </Button>
            <Button variant="link" className="share-btn">
              <FontAwesomeIcon icon={faShare} />
            </Button>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="panel-body">
        {/* Image Carousel */}
        {(() => {
          const images = Array.isArray(currentProperty.images) ? currentProperty.images : 
                        currentProperty.images ? [currentProperty.images] : [];
          
          if (images.length > 0) {
            return (
              <Carousel className="property-carousel">
                {images.map((image, idx) => (
                  <Carousel.Item key={idx}>
                    <img
                      className="d-block w-100"
                      src={image}
                      alt={`Property view ${idx + 1}`}
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/800x600/1f2937/ffffff?text=No+Image';
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            );
          }
          return null;
        })()}

        {/* Price and Address */}
        <div className="property-header">
          <div className="property-status">For Sale</div>
          <h2 className="property-price">{safeFormatPrice(currentProperty.price)}</h2>
          <div className="price-details">
            <span>${currentProperty.price && currentProperty.sqft ? Math.round(currentProperty.price / currentProperty.sqft) : 0}/sqft</span>
            {currentProperty.priceChange && (
              <span className={`price-change ${currentProperty.priceChange < 0 ? 'negative' : ''} ms-3`}>
                {currentProperty.priceChange > 0 ? '+' : ''}{currentProperty.priceChange}%
              </span>
            )}
          </div>
          <p className="property-address">
            {getAddressString(currentProperty)}
          </p>
        </div>

        {/* Key Stats */}
        <div className="property-stats">
          <div className="stat">
            <FontAwesomeIcon icon={faBed} />
            <span>{currentProperty.bedrooms || 0}</span>
            <label>Beds</label>
          </div>
          <div className="stat">
            <FontAwesomeIcon icon={faBath} />
            <span>{currentProperty.bathrooms || 0}</span>
            <label>Baths</label>
          </div>
          <div className="stat">
            <FontAwesomeIcon icon={faRuler} />
            <span>{currentProperty.sqft?.toLocaleString() || '0'}</span>
            <label>Sq Ft</label>
          </div>
          <div className="stat">
            <FontAwesomeIcon icon={faHome} />
            <span>{currentProperty.yearBuilt || 'N/A'}</span>
            <label>Built</label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <Button 
            variant="primary" 
            size="lg" 
            className="w-100 mb-2"
            onClick={handleScheduleTour}
          >
            <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
            Schedule Tour
          </Button>
          <Row>
            <Col xs={6}>
              <Button variant="outline-primary" className="w-100">
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                Call Agent
              </Button>
            </Col>
            <Col xs={6}>
              <Button variant="outline-primary" className="w-100">
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Message
              </Button>
            </Col>
          </Row>
        </div>

        {/* Property Details */}
        <Card className="property-details-card">
          <Card.Body>
            <h5>Property Details</h5>
            <ListGroup variant="flush">
              <ListGroup.Item className="detail-item">
                <span className="detail-label">MLS #</span>
                <span className="detail-value">{currentProperty.mlsNumber}</span>
              </ListGroup.Item>
              <ListGroup.Item className="detail-item">
                <span className="detail-label">Property Type</span>
                <span className="detail-value">{currentProperty.propertyType || 'Residential'}</span>
              </ListGroup.Item>
              <ListGroup.Item className="detail-item">
                <span className="detail-label">Days on Market</span>
                <span className="detail-value">
                  {currentProperty.daysOnMarket || 0}
                  {currentProperty.daysOnMarket && currentProperty.daysOnMarket <= 7 && (
                    <Badge bg="danger" className="ms-2">NEW</Badge>
                  )}
                </span>
              </ListGroup.Item>
              <ListGroup.Item className="detail-item">
                <span className="detail-label">Price per Sq Ft</span>
                <span className="detail-value">
                  ${currentProperty.price && currentProperty.sqft ? Math.round(currentProperty.price / currentProperty.sqft) : 0}
                </span>
              </ListGroup.Item>
              {currentProperty.garage && (
                <ListGroup.Item className="detail-item">
                  <span className="detail-label">Garage</span>
                  <span className="detail-value">{currentProperty.garage} Car</span>
                </ListGroup.Item>
              )}
              {currentProperty.lotSize && (
                <ListGroup.Item className="detail-item">
                  <span className="detail-label">Lot Size</span>
                  <span className="detail-value">
                    {currentProperty.lotSize?.toLocaleString() || '0'} sqft
                    {currentProperty.lotSize && currentProperty.lotSize >= 43560 && (
                      <span className="text-muted ms-1">
                        ({(currentProperty.lotSize / 43560).toFixed(2)} acres)
                      </span>
                    )}
                  </span>
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card.Body>
        </Card>

        {/* Features */}
        {currentProperty.features && Array.isArray(currentProperty.features) && currentProperty.features.length > 0 && (
          <Card className="property-features-card">
            <Card.Body>
              <h5>Features</h5>
              <div className="features-list">
                {currentProperty.features.map((feature, idx) => (
                  <Badge key={idx} bg="secondary" className="feature-badge">
                    {feature}
                  </Badge>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Listing Agent */}
        {currentProperty.listingAgent && (
          <Card className="agent-card">
            <Card.Body>
              <h5>Listing Agent</h5>
              <div className="agent-info">
                <div>
                  <strong>{currentProperty.listingAgent.name}</strong><br />
                  {currentProperty.listingAgent.officeName}
                </div>
                <Button variant="primary" size="sm">
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  Contact
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Theta AI Insights */}
        <Card className="ai-insights-card">
          <Card.Body>
            <h5>🤖 AI Property Insights</h5>
            <div className="ai-insight">
              <strong>Market Analysis:</strong> This property is priced competitively compared to
              similar {currentProperty.bedrooms}-bedroom homes in {getCity(currentProperty)}.
              {currentProperty.daysOnMarket && currentProperty.daysOnMarket <= 7 && (
                <Badge bg="success" className="ms-2">New Listing</Badge>
              )}
            </div>
            <div className="ai-insight">
              <strong>Investment Potential:</strong> Based on historical data, properties in this 
              area have seen an average appreciation of 8-12% annually.
            </div>
            {currentProperty.schoolRating && currentProperty.schoolRating >= 8 && (
              <div className="ai-insight">
                <strong>School District:</strong> Excellent schools nearby with ratings of {currentProperty.schoolRating}/10
              </div>
            )}
          </Card.Body>
        </Card>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default PropertyDetailsPanel;